"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.newObjectState = exports.createObjectState = void 0;
const mobx_1 = require("mobx");
const listField_1 = require("./listField");
const valueField_1 = require("./valueField");
const utils_1 = require("../utils");
/**
 * Creates a new `ObjectState` for a given form object `T` given config rules in `config`.
 *
 * The returned `ObjectState` can be used in a mobx `useLocalObservable` to drive an
 * interactive form that tracks the current valid/touched/etc. state of both each
 * individual fields as well as the top-level form/object itself.
 */
function createObjectState(config, instance, opts = {}) {
    const noop = () => { };
    return newObjectState(config, undefined, undefined, instance, undefined, opts.maybeAutoSave || noop);
}
exports.createObjectState = createObjectState;
function newObjectState(config, parentState, parentListState, instance, key, maybeAutoSave) {
    // This is what we return, but we only know it's value until we call `observable`, so
    // we create a mutable variable to capture it so that we can create fields/call their
    // constructors and give them a way to access it later.
    let proxy = undefined;
    function getObjectState() {
        if (!proxy) {
            throw new Error("Race condition");
        }
        return proxy;
    }
    const fieldStates = Object.entries(config).map(([_key, _config]) => {
        var _a;
        const key = _key;
        const config = _config;
        let field;
        if (config.type === "value") {
            field = valueField_1.newValueFieldState(instance, getObjectState, key, config.rules || [], config.isIdKey || key === "id", config.isDeleteKey || false, config.isReadOnlyKey || false, config.computed || false, config.readOnly || false, (_a = config.strictOrder) !== null && _a !== void 0 ? _a : true, maybeAutoSave);
        }
        else if (config.type === "list") {
            field = listField_1.newListFieldState(instance, getObjectState, key, config.rules || [], config, config.config, maybeAutoSave);
        }
        else if (config.type === "object") {
            if (!instance[key]) {
                instance[key] = {};
            }
            field = newObjectState(config.config, getObjectState, undefined, instance[key], key, maybeAutoSave);
        }
        else {
            throw new Error("Unsupported");
        }
        return [key, field];
    });
    // We always return the same `instance` field from our `value` method, but
    // we want to pretend that it's observable, so use a tick to force it.
    const _tick = mobx_1.observable({ value: 1 });
    const fieldNames = Object.keys(config);
    function getFields(proxyThis) {
        return fieldNames.map((name) => proxyThis[name]);
    }
    const obj = {
        ...Object.fromEntries(fieldStates),
        key,
        get value() {
            _tick.value > 0 || utils_1.fail();
            return instance;
        },
        set value(value) {
            this.set(value);
        },
        // private
        _readOnly: false,
        _loading: false,
        _considerDeleted() {
            const deleteField = getFields(this).find((f) => f._isDeleteKey);
            return !!(deleteField === null || deleteField === void 0 ? void 0 : deleteField.value);
        },
        _considerReadOnly() {
            const readOnlyField = getFields(this).find((f) => f._isReadOnlyKey);
            return !!(readOnlyField === null || readOnlyField === void 0 ? void 0 : readOnlyField.value);
        },
        get touched() {
            return getFields(this).some((f) => f.touched);
        },
        set touched(touched) {
            getFields(this).forEach((f) => (f.touched = touched));
        },
        get readOnly() {
            return (this._readOnly ||
                this._considerReadOnly() ||
                (parentState && parentState().readOnly) ||
                !!(parentListState === null || parentListState === void 0 ? void 0 : parentListState.readOnly));
        },
        set readOnly(readOnly) {
            this._readOnly = readOnly;
        },
        get loading() {
            return this._loading || (parentState && parentState().loading) || !!(parentListState === null || parentListState === void 0 ? void 0 : parentListState.loading);
        },
        set loading(loading) {
            this._loading = loading;
        },
        get valid() {
            return getFields(this).every((f) => f.valid);
        },
        get errors() {
            return getFields(this).flatMap((f) => f.errors.map((e) => `${f.key}: ${e}`));
        },
        get dirty() {
            return getFields(this).some((f) => f.dirty);
        },
        get isNewEntity() {
            const idField = getFields(this).find((f) => f._isIdKey);
            // If we're a line item w/o an immediate id field, look in our parent
            if (!idField && parentState) {
                return parentState().isNewEntity;
            }
            // If there is no id field, assume we're not new
            return idField !== undefined && (idField.value === null || idField.value === undefined);
        },
        canSave() {
            this.touched = true;
            return this.valid;
        },
        // Accepts new values in bulk, i.e. when setting the form initial state from the backend.
        set(value, opts = {}) {
            if (this.readOnly && !opts.resetting && !opts.refreshing) {
                throw new Error(`${key || "formState"} is currently readOnly`);
            }
            getFields(this).forEach((field) => {
                if (field.key in value) {
                    field.set(value[field.key], opts);
                }
            });
        },
        // Resets all fields back to their original values
        revertChanges() {
            getFields(this).forEach((f) => f.revertChanges());
        },
        // Saves all current values into _originalValue
        commitChanges() {
            getFields(this).forEach((f) => f.commitChanges());
        },
        // Create a result that is only populated with changed keys
        get changedValue() {
            const result = {};
            getFields(this).forEach((f) => {
                if (f.dirty ||
                    // If the caller used useFormState.ifUndefined to provide some default values, then those keys may not
                    // look dirty, but if we're new we should include them anyway.
                    (this.isNewEntity &&
                        // Unless they're undefined anyway
                        f.value !== undefined &&
                        // And unless they're empty sub-objects
                        !(f.value instanceof Object && Object.entries(f.changedValue).length === 0))) {
                    result[f.key] = f.changedValue;
                }
            });
            // Ensure we always have the id for updates to work
            const idField = getFields(this).find((f) => f._isIdKey);
            if (idField && idField.value !== undefined) {
                result[idField.key] = idField.value;
            }
            return result;
        },
        get originalValue() {
            return instance;
        },
        // An internal helper method to see if `other` is for "the same entity" as our current row
        isSameEntity(other) {
            const idField = getFields(this).find((f) => f._isIdKey);
            if (!idField) {
                return false;
            }
            return this[idField.key].value === other[idField.key];
        },
    };
    proxy = mobx_1.makeAutoObservable(obj, {
        // Use custom equality on `value` that is _never_ equals. This sounds weird, but
        // because our `value` is always the same `instance` that was passed to `newObjectState`,
        // to mobx this looks like the value never changes, and it will never invoke observers
        // even with our tick-based hacks.
        value: mobx_1.computed({ equals: () => false }),
    });
    // Any time a field changes, percolate that change up to us
    mobx_1.reaction(() => getFields(proxy).map((f) => f.value), () => _tick.value++);
    return proxy;
}
exports.newObjectState = newObjectState;
